import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, IconButton, Stack, Chip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Archive as ArchiveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { projectsApi, driversApi, workItemsApi, authApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type {
  Project, ProjectStatus, RAGStatus, ProjectMember, ProjectMemberRole,
  ProjectOutcome, OutcomeStatus, ProjectIndicator, IndicatorType, IndicatorDirection,
  AnnualStrategicDriver, WorkItem,
} from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';

const ragValues: RAGStatus[] = ['Green', 'Amber', 'Red'];
const statusValues: ProjectStatus[] = ['Planning', 'Active', 'OnHold', 'Completed', 'Cancelled'];
const outcomeStatuses: OutcomeStatus[] = ['NotStarted', 'InProgress', 'Achieved', 'Missed', 'Deferred'];
const memberRoles: ProjectMemberRole[] = ['Owner', 'Sponsor', 'Contributor', 'Informed'];

const ragColorMap: Record<RAGStatus, 'success' | 'warning' | 'error'> = {
  Green: 'success',
  Amber: 'warning',
  Red: 'error',
};

const statusColorMap: Record<ProjectStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Planning: 'info',
  Active: 'success',
  OnHold: 'warning',
  Completed: 'default',
  Cancelled: 'error',
};

const outcomeColorMap: Record<OutcomeStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  NotStarted: 'default',
  InProgress: 'info',
  Achieved: 'success',
  Missed: 'error',
  Deferred: 'warning',
};

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [outcomes, setOutcomes] = useState<ProjectOutcome[]>([]);
  const [indicators, setIndicators] = useState<ProjectIndicator[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [report, setReport] = useState<any>(null);
  const [users, setUsers] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars

  const [tabValue, setTabValue] = useState(0);

  // Edit project dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState<Partial<Project>>({});

  // Add member dialog
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<ProjectMemberRole>('Contributor');

  // Add outcome dialog
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [newOutcome, setNewOutcome] = useState<Partial<ProjectOutcome>>({ title: '', description: '', targetDate: '' });

  // Add indicator dialog
  const [indicatorDialogOpen, setIndicatorDialogOpen] = useState(false);
  const [newIndicator, setNewIndicator] = useState<Partial<ProjectIndicator>>({
    title: '', description: '', indicatorType: 'Leading', unit: '', targetValue: undefined, direction: 'HigherIsBetter',
  });

  // Record value dialog
  const [recordValueDialogOpen, setRecordValueDialogOpen] = useState(false);
  const [recordIndicatorId, setRecordIndicatorId] = useState('');
  const [recordValue, setRecordValue] = useState<number | string>('');
  const [recordNotes, setRecordNotes] = useState('');

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projectData, membersData, outcomesData, indicatorsData, driversData] = await Promise.all([
        projectsApi.getById(projectId),
        projectsApi.getMembers(projectId),
        projectsApi.getOutcomes(projectId),
        projectsApi.getIndicators(projectId),
        driversApi.getActive(),
      ]);
      setProject(projectData);
      setMembers(membersData);
      setOutcomes(outcomesData);
      setIndicators(indicatorsData);
      setDrivers(driversData);

      // Load work items for this project
      try {
        const allItems = await workItemsApi.getAll();
        setWorkItems(allItems.filter(wi => wi.projectId === projectId));
      } catch {
        setWorkItems([]);
      }

      // Load report
      try {
        setReport(await projectsApi.getReport(projectId));
      } catch {
        setReport(null);
      }

      // Load tenant users for member add
      try {
        const usersData = await authApi.getUsers();
        setUsers(usersData.map((u: any) => ({ id: u.id, displayName: u.displayName, email: u.email })));
      } catch {
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to load project', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // RAG cycle
  const handleRagCycle = async () => {
    if (!project) return;
    const idx = ragValues.indexOf(project.ragStatus);
    const next = ragValues[(idx + 1) % ragValues.length];
    try {
      await projectsApi.update(project.id, { ragStatus: next });
      setProject(prev => prev ? { ...prev, ragStatus: next } : prev);
    } catch (err) {
      console.error('Failed to update RAG status', err);
    }
  };

  // Status cycle
  const handleStatusCycle = async () => {
    if (!project) return;
    const idx = statusValues.indexOf(project.status);
    const next = statusValues[(idx + 1) % statusValues.length];
    try {
      await projectsApi.update(project.id, { status: next });
      setProject(prev => prev ? { ...prev, status: next } : prev);
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  // Edit project
  const handleEditSave = async () => {
    if (!project) return;
    try {
      const updated = await projectsApi.update(project.id, editProject);
      setProject(updated);
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update project', err);
    }
  };

  // Archive
  const handleArchive = async () => {
    if (!project || !window.confirm('Archive this project?')) return;
    try {
      await projectsApi.archive(project.id);
      navigate('/projects');
    } catch (err) {
      console.error('Failed to archive project', err);
    }
  };

  // Members
  const handleAddMember = async () => {
    if (!project || !newMemberUserId) return;
    try {
      await projectsApi.addMember(project.id, { userId: newMemberUserId, role: newMemberRole });
      setMemberDialogOpen(false);
      setNewMemberUserId('');
      setNewMemberRole('Contributor');
      setMembers(await projectsApi.getMembers(project.id));
    } catch (err) {
      console.error('Failed to add member', err);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!project || !window.confirm('Remove this member?')) return;
    try {
      await projectsApi.removeMember(project.id, memberId);
      setMembers(await projectsApi.getMembers(project.id));
    } catch (err) {
      console.error('Failed to remove member', err);
    }
  };

  // Outcomes
  const handleAddOutcome = async () => {
    if (!project || !newOutcome.title) return;
    try {
      await projectsApi.createOutcome(project.id, {
        ...newOutcome,
        targetDate: newOutcome.targetDate || undefined,
      });
      setOutcomeDialogOpen(false);
      setNewOutcome({ title: '', description: '', targetDate: '' });
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err) {
      console.error('Failed to add outcome', err);
    }
  };

  const handleOutcomeStatusCycle = async (outcome: ProjectOutcome) => {
    if (!project) return;
    const idx = outcomeStatuses.indexOf(outcome.status);
    const next = outcomeStatuses[(idx + 1) % outcomeStatuses.length];
    try {
      await projectsApi.updateOutcome(project.id, outcome.id, { status: next });
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err) {
      console.error('Failed to update outcome status', err);
    }
  };

  const handleDeleteOutcome = async (outcomeId: string) => {
    if (!project || !window.confirm('Delete this outcome?')) return;
    try {
      await projectsApi.deleteOutcome(project.id, outcomeId);
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err) {
      console.error('Failed to delete outcome', err);
    }
  };

  // Indicators
  const handleAddIndicator = async () => {
    if (!project || !newIndicator.title) return;
    try {
      await projectsApi.createIndicator(project.id, newIndicator);
      setIndicatorDialogOpen(false);
      setNewIndicator({ title: '', description: '', indicatorType: 'Leading', unit: '', targetValue: undefined, direction: 'HigherIsBetter' });
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err) {
      console.error('Failed to add indicator', err);
    }
  };

  const handleDeleteIndicator = async (indicatorId: string) => {
    if (!project || !window.confirm('Delete this indicator?')) return;
    try {
      await projectsApi.deleteIndicator(project.id, indicatorId);
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err) {
      console.error('Failed to delete indicator', err);
    }
  };

  const handleRecordValue = async () => {
    if (!project || !recordIndicatorId || recordValue === '') return;
    try {
      await projectsApi.recordValue(project.id, recordIndicatorId, {
        value: Number(recordValue),
        notes: recordNotes || undefined,
      });
      setRecordValueDialogOpen(false);
      setRecordIndicatorId('');
      setRecordValue('');
      setRecordNotes('');
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err) {
      console.error('Failed to record value', err);
    }
  };

  // Indicator helpers
  const isIndicatorMeetingTarget = (indicator: ProjectIndicator): boolean | null => {
    if (indicator.currentValue == null || indicator.targetValue == null) return null;
    if (indicator.direction === 'HigherIsBetter') return indicator.currentValue >= indicator.targetValue;
    return indicator.currentValue <= indicator.targetValue;
  };

  const leadingIndicators = useMemo(() => indicators.filter(i => i.indicatorType === 'Leading'), [indicators]);
  const laggingIndicators = useMemo(() => indicators.filter(i => i.indicatorType === 'Lagging'), [indicators]);

  const driverTitle = useMemo(() => {
    if (!project?.strategicDriverId) return null;
    return drivers.find(d => d.id === project.strategicDriverId)?.title;
  }, [project, drivers]);

  const getDaysRemaining = () => {
    if (!project?.targetEndDate) return null;
    const target = new Date(project.targetEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatMinutes = (mins: number): string => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (!project) {
    return (
      <Box>
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {loading ? 'Loading project...' : 'Project not found'}
        </Typography>
      </Box>
    );
  }

  const daysRemaining = getDaysRemaining();
  const outcomeAchieved = outcomes.filter(o => o.status === 'Achieved').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/projects')} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flex: 1 }}>{project.title}</Typography>
        <Chip
          label={project.ragStatus}
          color={ragColorMap[project.ragStatus]}
          onClick={handleRagCycle}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          label={project.status}
          variant="outlined"
          color={statusColorMap[project.status]}
          onClick={handleStatusCycle}
          sx={{ cursor: 'pointer' }}
        />
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => {
            setEditProject({
              title: project.title,
              description: project.description,
              strategicDriverId: project.strategicDriverId,
              startDate: project.startDate,
              targetEndDate: project.targetEndDate,
              progressPercentage: project.progressPercentage,
              progressMode: project.progressMode,
              notes: project.notes,
            });
            setEditDialogOpen(true);
          }}
        >
          Edit
        </Button>
        <Button size="small" startIcon={<ArchiveIcon />} color="warning" onClick={handleArchive}>
          Archive
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label={`Outcomes (${outcomes.length})`} />
        <Tab label={`Indicators (${indicators.length})`} />
        <Tab label={`Work Items (${workItems.length})`} />
        <Tab label="Time Invested" />
      </Tabs>

      {/* Tab 0: Overview */}
      {tabValue === 0 && (
        <Box>
          {project.description && (
            <Typography variant="body1" sx={{ mb: 2 }}>{project.description}</Typography>
          )}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
                {project.startDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Start Date</Typography>
                    <Typography variant="body2" fontWeight={500}>{project.startDate}</Typography>
                  </Box>
                )}
                {project.targetEndDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Target End Date</Typography>
                    <Typography variant="body2" fontWeight={500}>{project.targetEndDate}</Typography>
                  </Box>
                )}
                {daysRemaining !== null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip
                      label={
                        daysRemaining < 0
                          ? `${Math.abs(daysRemaining)} days overdue`
                          : daysRemaining === 0
                            ? 'Due today'
                            : `${daysRemaining} days remaining`
                      }
                      size="small"
                      color={daysRemaining < 0 ? 'error' : daysRemaining <= 7 ? 'warning' : 'default'}
                      sx={{ mt: 0.25 }}
                    />
                  </Box>
                )}
              </Stack>

              {driverTitle && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Strategic Driver</Typography>
                  <Box>
                    <Chip
                      label={driverTitle}
                      color="primary"
                      variant="outlined"
                      onClick={() => navigate('/strategy')}
                      sx={{ cursor: 'pointer', mt: 0.5 }}
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={project.progressMode} size="small" variant="outlined" />
                    <Typography variant="caption" fontWeight={600}>{project.progressPercentage}%</Typography>
                  </Stack>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={project.progressPercentage}
                  sx={{ height: 8, borderRadius: 4 }}
                  color={project.ragStatus === 'Red' ? 'error' : project.ragStatus === 'Amber' ? 'warning' : 'success'}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Members */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">Members</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setMemberDialogOpen(true)}>
                  Add Member
                </Button>
              </Box>
              {members.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No members yet</Typography>
              ) : (
                <Stack spacing={1}>
                  {members.map(m => (
                    <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                        {m.displayName || m.email || m.userId}
                      </Typography>
                      <Chip label={m.role} size="small" variant="outlined" />
                      <IconButton size="small" onClick={() => handleRemoveMember(m.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab 1: Outcomes */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {outcomeAchieved} of {outcomes.length} outcomes achieved
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setOutcomeDialogOpen(true)}>
              Add Outcome
            </Button>
          </Box>

          {outcomes.length === 0 ? (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No outcomes defined yet</Typography>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {outcomes.map(outcome => (
                <Card key={outcome.id}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={500}>{outcome.title}</Typography>
                        {outcome.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {outcome.description}
                          </Typography>
                        )}
                        {outcome.targetDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Target: {outcome.targetDate}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={outcome.status}
                          size="small"
                          color={outcomeColorMap[outcome.status]}
                          onClick={() => handleOutcomeStatusCycle(outcome)}
                          sx={{ cursor: 'pointer' }}
                        />
                        <IconButton size="small" onClick={() => handleDeleteOutcome(outcome.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Tab 2: Indicators */}
      {tabValue === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setIndicatorDialogOpen(true)}>
              Add Indicator
            </Button>
          </Box>

          {indicators.length === 0 ? (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No indicators defined yet</Typography>
            </Card>
          ) : (
            <>
              {leadingIndicators.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>Leading Indicators</Typography>
                  <Stack spacing={1.5}>
                    {leadingIndicators.map(ind => {
                      const meetingTarget = isIndicatorMeetingTarget(ind);
                      return (
                        <Card key={ind.id}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight={500}>{ind.title}</Typography>
                                {ind.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {ind.description}
                                  </Typography>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ color: meetingTarget === true ? 'success.main' : meetingTarget === false ? 'error.main' : 'text.primary' }}
                                  >
                                    {ind.currentValue != null ? ind.currentValue : '-'}
                                    {ind.unit ? ` ${ind.unit}` : ''}
                                  </Typography>
                                  {ind.targetValue != null && (
                                    <Typography variant="caption" color="text.secondary">
                                      / {ind.targetValue}{ind.unit ? ` ${ind.unit}` : ''}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {ind.direction === 'HigherIsBetter' ? '\u2191' : '\u2193'}
                                  </Typography>
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setRecordIndicatorId(ind.id);
                                    setRecordValue('');
                                    setRecordNotes('');
                                    setRecordValueDialogOpen(true);
                                  }}
                                >
                                  Record
                                </Button>
                                <IconButton size="small" onClick={() => handleDeleteIndicator(ind.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {laggingIndicators.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>Lagging Indicators</Typography>
                  <Stack spacing={1.5}>
                    {laggingIndicators.map(ind => {
                      const meetingTarget = isIndicatorMeetingTarget(ind);
                      return (
                        <Card key={ind.id}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight={500}>{ind.title}</Typography>
                                {ind.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {ind.description}
                                  </Typography>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ color: meetingTarget === true ? 'success.main' : meetingTarget === false ? 'error.main' : 'text.primary' }}
                                  >
                                    {ind.currentValue != null ? ind.currentValue : '-'}
                                    {ind.unit ? ` ${ind.unit}` : ''}
                                  </Typography>
                                  {ind.targetValue != null && (
                                    <Typography variant="caption" color="text.secondary">
                                      / {ind.targetValue}{ind.unit ? ` ${ind.unit}` : ''}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {ind.direction === 'HigherIsBetter' ? '\u2191' : '\u2193'}
                                  </Typography>
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setRecordIndicatorId(ind.id);
                                    setRecordValue('');
                                    setRecordNotes('');
                                    setRecordValueDialogOpen(true);
                                  }}
                                >
                                  Record
                                </Button>
                                <IconButton size="small" onClick={() => handleDeleteIndicator(ind.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Tab 3: Work Items */}
      {tabValue === 3 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/work-items?projectId=${projectId}`)}
            >
              Add Work Item
            </Button>
          </Box>

          {workItems.length === 0 ? (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No work items linked to this project</Typography>
            </Card>
          ) : (
            <TableContainer component={Card}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Target Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workItems.map(item => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                      </TableCell>
                      <TableCell><Chip label={item.type} size="small" variant="outlined" /></TableCell>
                      <TableCell><PriorityChip priority={item.priority} /></TableCell>
                      <TableCell><StatusChip status={item.status} /></TableCell>
                      <TableCell>{item.targetDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab 4: Time Invested */}
      {tabValue === 4 && (
        <Box>
          {!report ? (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No time data available yet</Typography>
            </Card>
          ) : (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>Total Time Invested</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {formatMinutes(report.timeInvested?.totalMinutes || 0)}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Work Items: {formatMinutes(report.timeInvested?.workItemMinutes || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Meetings: {formatMinutes(report.timeInvested?.meetingMinutes || 0)}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              {report.timeInvested?.byWeek && report.timeInvested.byWeek.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Weekly Breakdown</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Week Start</TableCell>
                            <TableCell align="right">Work Items</TableCell>
                            <TableCell align="right">Meetings</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.timeInvested.byWeek.map((week: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{week.weekStart}</TableCell>
                              <TableCell align="right">{formatMinutes(week.workMinutes || 0)}</TableCell>
                              <TableCell align="right">{formatMinutes(week.meetingMinutes || 0)}</TableCell>
                              <TableCell align="right">{formatMinutes((week.workMinutes || 0) + (week.meetingMinutes || 0))}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {report.timeInvested?.byMember && report.timeInvested.byMember.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Member Breakdown</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="right">Total Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.timeInvested.byMember.map((member: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{member.name}</TableCell>
                              <TableCell><Chip label={member.role} size="small" variant="outlined" /></TableCell>
                              <TableCell align="right">{formatMinutes(member.totalMinutes || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Box>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              value={editProject.title || ''}
              onChange={e => setEditProject(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editProject.description || ''}
              onChange={e => setEditProject(prev => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Strategic Driver</InputLabel>
              <Select
                value={editProject.strategicDriverId || ''}
                label="Strategic Driver"
                onChange={e => setEditProject(prev => ({ ...prev, strategicDriverId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {drivers.map(d => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editProject.startDate || ''}
                onChange={e => setEditProject(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <TextField
                label="Target End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editProject.targetEndDate || ''}
                onChange={e => setEditProject(prev => ({ ...prev, targetEndDate: e.target.value }))}
              />
            </Stack>
            <TextField
              label="Progress (%)"
              type="number"
              fullWidth
              inputProps={{ min: 0, max: 100 }}
              value={editProject.progressPercentage ?? 0}
              onChange={e => setEditProject(prev => ({ ...prev, progressPercentage: parseFloat(e.target.value) || 0 }))}
            />
            <FormControl fullWidth>
              <InputLabel>Progress Mode</InputLabel>
              <Select
                value={editProject.progressMode || 'Manual'}
                label="Progress Mode"
                onChange={e => setEditProject(prev => ({ ...prev, progressMode: e.target.value as 'Manual' | 'AutoCalculated' }))}
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="AutoCalculated">Auto Calculated</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={editProject.notes || ''}
              onChange={e => setEditProject(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={newMemberUserId}
                label="User"
                onChange={e => setNewMemberUserId(e.target.value)}
              >
                {users
                  .filter(u => !members.some(m => m.userId === u.id))
                  .map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.displayName} ({u.email})</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newMemberRole}
                label="Role"
                onChange={e => setNewMemberRole(e.target.value as ProjectMemberRole)}
              >
                {memberRoles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember} disabled={!newMemberUserId}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Outcome Dialog */}
      <Dialog open={outcomeDialogOpen} onClose={() => setOutcomeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Outcome</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={newOutcome.title || ''}
              onChange={e => setNewOutcome(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newOutcome.description || ''}
              onChange={e => setNewOutcome(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Target Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newOutcome.targetDate || ''}
              onChange={e => setNewOutcome(prev => ({ ...prev, targetDate: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddOutcome} disabled={!newOutcome.title}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Indicator Dialog */}
      <Dialog open={indicatorDialogOpen} onClose={() => setIndicatorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Indicator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={newIndicator.title || ''}
              onChange={e => setNewIndicator(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newIndicator.description || ''}
              onChange={e => setNewIndicator(prev => ({ ...prev, description: e.target.value }))}
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newIndicator.indicatorType || 'Leading'}
                  label="Type"
                  onChange={e => setNewIndicator(prev => ({ ...prev, indicatorType: e.target.value as IndicatorType }))}
                >
                  <MenuItem value="Leading">Leading</MenuItem>
                  <MenuItem value="Lagging">Lagging</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={newIndicator.direction || 'HigherIsBetter'}
                  label="Direction"
                  onChange={e => setNewIndicator(prev => ({ ...prev, direction: e.target.value as IndicatorDirection }))}
                >
                  <MenuItem value="HigherIsBetter">Higher is Better</MenuItem>
                  <MenuItem value="LowerIsBetter">Lower is Better</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Unit"
                fullWidth
                placeholder="e.g. %, days, count"
                value={newIndicator.unit || ''}
                onChange={e => setNewIndicator(prev => ({ ...prev, unit: e.target.value }))}
              />
              <TextField
                label="Target Value"
                type="number"
                fullWidth
                value={newIndicator.targetValue ?? ''}
                onChange={e => setNewIndicator(prev => ({ ...prev, targetValue: e.target.value ? parseFloat(e.target.value) : undefined }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIndicatorDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddIndicator} disabled={!newIndicator.title}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Record Value Dialog */}
      <Dialog open={recordValueDialogOpen} onClose={() => setRecordValueDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Value</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Value"
              type="number"
              fullWidth
              required
              value={recordValue}
              onChange={e => setRecordValue(e.target.value)}
            />
            <TextField
              label="Notes (optional)"
              fullWidth
              multiline
              rows={2}
              value={recordNotes}
              onChange={e => setRecordNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordValueDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRecordValue} disabled={recordValue === ''}>Record</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
